<script setup lang="ts">
import { ref, watch } from 'vue';
import { cloneDeep } from 'lodash';

import type { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';
import { getBoardBasisText } from '@apps/main/new-desktop/core/helpers/translation.helper';

import { AppMultiSelect } from '@libs/ui';
import type { AppMultiSelectOption } from '@libs/ui';
import { BoardBasisCode } from '@libs/trip-planner-interfaces';

import type {
  HotelDetailsFullscreenMasonryGalleryData,
  Stay,
  StayId,
} from '../../models';
import { StaySelectionType } from '../../models';

import HotelStay from './HotelStay.vue';

interface HotelDetailsStaysSectionProps {
  alternativeStays: Stay[];
  selectedStay?: Stay;
  source: HotelDetailsEventSource;
}
const props = defineProps<HotelDetailsStaysSectionProps>();

interface HotelDetailsStaysSectionEmits {
  (eventName: 'stay-selected', stayId: StayId): void;
  (eventName: 'more-information', stay: Stay): void;
  (
    eventName: 'open-gallery',
    data: HotelDetailsFullscreenMasonryGalleryData
  ): void;
}

const emit = defineEmits<HotelDetailsStaysSectionEmits>();

const root = ref<HTMLElement>();

let allBoardBasisOptions: AppMultiSelectOption<string>[] = [
  { id: 'RO', label: getBoardBasisText(BoardBasisCode.RO), disabled: true },
  { id: 'BB', label: getBoardBasisText(BoardBasisCode.BB), disabled: true },
  { id: 'HB', label: getBoardBasisText(BoardBasisCode.HB), disabled: true },
  { id: 'FB', label: getBoardBasisText(BoardBasisCode.FB), disabled: true },
  { id: 'AI', label: getBoardBasisText(BoardBasisCode.AI), disabled: true },
];

let boardBasisOptions = ref<AppMultiSelectOption<string>[]>([]);
watch(
  () => props,
  () => {
    const allBoardsInHotel = new Set();
    props.alternativeStays.forEach((stay) => {
      stay.rooms.forEach((room) => {
        allBoardsInHotel.add(room.boardBasis);
      });
    });
    if (props.selectedStay) {
      props.selectedStay.rooms.forEach((room) => {
        allBoardsInHotel.add(room.boardBasis);
      });
    }

    const calculatedOptions = cloneDeep(allBoardBasisOptions);
    calculatedOptions.forEach((option) => {
      option.disabled = !allBoardsInHotel.has(option.id);
    });
    boardBasisOptions.value = calculatedOptions;
  },
  { immediate: true, deep: true }
);

function onSelectOption(selectedOptions: AppMultiSelectOption<string>[]): void {
  boardBasisOptions.value = cloneDeep(selectedOptions);
}

defineExpose({
  root,
});
</script>

<template>
  <div id="hotel-details-stays" ref="root" class="hotel-details-stays-section">
    <!-- Un if when doing hotel rooms filter -->
    <div v-if="false" class="hotel-stay-filters">
      <AppMultiSelect
        type="primary"
        :options="boardBasisOptions"
        :use-teleport="false"
        placeholder="Show all meals"
        placeholder-selected="meals plans selected"
        @select="onSelectOption($event)"
      />
    </div>
    <div v-if="props.selectedStay" class="hotel-stay-container">
      <div class="hotel-stay-title">
        {{
          props.selectedStay.selectionType ===
          StaySelectionType.ManuallySelected
            ? $t('planner.selected_room')
            : $t('planner.suggested_room')
        }}
      </div>

      <HotelStay
        :stay="props.selectedStay"
        :show-select-button="
          props.selectedStay.selectionType === StaySelectionType.Suggested
        "
        :source="props.source"
        @more-information="emit('more-information', props.selectedStay)"
        @stay-selected="emit('stay-selected', $event)"
        @open-gallery="emit('open-gallery', $event)"
      />
    </div>

    <div v-if="props.alternativeStays.length" class="hotel-stay-container">
      <div class="hotel-stay-title">
        {{
          props.selectedStay
            ? $t('planner.more_alternatives')
            : $t('planner.select_room')
        }}
      </div>
      <HotelStay
        v-for="(stay, index) in props.alternativeStays"
        :key="index"
        :show-select-button="true"
        :stay="stay"
        :source="props.source"
        @stay-selected="emit('stay-selected', $event)"
        @more-information="emit('more-information', stay)"
        @open-gallery="emit('open-gallery', $event)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-details-stays-section {
  padding-bottom: var(--spacing-32);
  border-bottom-left-radius: var(--border-radius-8);
  border-bottom-right-radius: var(--border-radius-8);

  @include for-large-devices-max {
    padding: var(--spacing-16) var(--spacing-16) var(--spacing-32)
      var(--spacing-16);
  }

  .hotel-stay-filters {
    display: flex;
    border-top: 1px solid var(--color-secondary-90);
    padding: var(--spacing-32) 0;
  }

  .hotel-stay-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-16);

    &:not(:last-child) {
      padding-bottom: var(--spacing-40);
    }

    .hotel-stay-title {
      color: var(--black);
      @include font-label-xxl-light;
    }
  }
}
</style>
